import * as React from 'react';
import * as ReactDOM from 'react-dom';

if (typeof window !== 'undefined') {
    window.React = React;
    window.ReactDOM = ReactDOM;
}

import asyncRender from './asyncRender';
import { ErrorBoundary } from 'owa-error-boundary';
import initializeRouting from './initializeRouting';
import initializeSatchelMiddleware from './initializeSatchelMiddleware';

import initializeWindowTitle from './initializeWindowTitle';
import initializeDateTime from './initializeDateTime';
import initializeLocales from './initializeLocales';
import setUserConfiguration from 'owa-session-store/lib/actions/setUserConfiguration';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { registerRoute, RouterOptions } from 'owa-router';
import { checkAndHandleOwaHipError } from 'owa-service-error';
import { updateServiceConfig } from 'owa-service/lib/config';
import { AnalyticsOptions } from 'owa-analytics';
import { addAnalyticsCallbacks } from 'owa-analytics-actions';
import { initializeFeatureFlags, isFeatureEnabled, FeatureName } from 'owa-feature-flags';
import { markFunction, trackBottleneck, PromiseWithKey } from 'owa-performance';
import { OwaWorkload, setOwaWorkload } from 'owa-workloads';
import { stashSessionDataResponses } from 'owa-session-store/lib/services/sessionDataResponseStash';
import { registerFontSubsets, IconFontRegistration } from 'owa-icons';
import { registerDefaultFontFaces } from '@uifabric/styling';
import { lazyIsUserIdle } from 'owa-useractivity-manager';
import { PreBootOptions, getPackageBaseUrl, getDefaultCdnSettings } from 'owa-config';
import { Middleware } from 'satcheljs';
import { LegacyMiddleware } from 'satcheljs/lib/legacy';
import { unblockLazyLoadCallbacks, LazyAction } from 'owa-bundling-light';
import { setBundlingConfig } from 'owa-bundling';
import { lazySetupSharedPostBoot } from 'owa-shared-post-boot';
import { registerCreateServiceResponseCallback as registerOwsPrimeCreateServiceResponseCallback } from 'owa-ows-gateway/lib/registerCreateServiceResponseCallback';

setBundlingConfig();

const DelayAfterRenderInMilliseconds = 100;

export interface BootstrapOptions {
    initializeState: () => void;
    renderMainComponent: () => JSX.Element;
    initialConfig: PreBootOptions;
    analyticsOptions?: AnalyticsOptions;
    registerRoutes?: (registerRouteFunction: typeof registerRoute) => Promise<void>;
    routerOptions?: RouterOptions;
    getWindowTitle: () => string;
    workload: OwaWorkload;
    iconFonts?: IconFontRegistration[];
    initializeLocalization: (culture: string, bootStrings: Record<string, string>) => Promise<void>;
    satchelMiddleware?: Middleware[];
    satchelLegacyMiddleware?: LegacyMiddleware[];
    postLazyAction?: LazyAction<() => void, any>;

    /**
     * Component to render when the app-wide error boundary catches an error
     */
    fullErrorComponent: React.ComponentType<{ error: Error }>;
    /**
     * Set of promises that must resolve before starting initial render
     */
    prerenderPromises?: PromiseWithKey<unknown>[];
    onComponentError?: () => void;
    swConfig?: {
        app: string;
        feature?: FeatureName;
    };
}

/**
 *  Bootstraps the application
 *  See [README.md](../../../../README.md) for details on boot flow
 */
export default markFunction(bootstrap, 'btsp');

let idleFn: () => boolean = null;

function bootstrap(options: BootstrapOptions): Promise<any> {
    addAnalyticsCallbacks(registerOwsPrimeCreateServiceResponseCallback);
    registerDefaultFontFaces(`${getDefaultCdnSettings().CdnBaseUrl}assets/mail/fonts/v1`);
    setOwaWorkload(options.workload || OwaWorkload.None);

    initializeSatchelMiddleware(options.satchelMiddleware, options.satchelLegacyMiddleware);
    registerFontSubsets(getPackageBaseUrl(), options.iconFonts);

    updateServiceConfig({
        onHipChallengeNeeded: (responseHeaders: Headers): boolean => {
            if (!isFeatureEnabled('fwk-hipExceptionRedirect')) {
                return false;
            }

            return checkAndHandleOwaHipError(responseHeaders);
        },
        isUserIdle: () => {
            if (idleFn) {
                return idleFn();
            } else {
                lazyIsUserIdle.import().then(f => (idleFn = f));
                return false;
            }
        },
    });

    let sessionDataPromise = options.initialConfig.sessionDataPromise;
    let userBootSettingsPromise = options.initialConfig.userBootSettingsPromise.then(settings => {
        if (settings.cdnSettings) {
            try {
                settings.cdnSettings.ExtraSettings = JSON.parse(
                    settings.cdnSettings.ExtraSettings as string
                );
            } catch {
                settings.cdnSettings.ExtraSettings = {};
            }
        }
        return settings;
    });
    let bootStringsPromise = options.initialConfig.bootStringsPromise;

    let renderPromise = trackBottleneck('bt', [
        { promise: sessionDataPromise, key: 'sd' },
        { promise: userBootSettingsPromise, key: 'ubs' },
        { promise: bootStringsPromise, key: 'bs' },
    ])
        .then(async data => {
            const [sessionData, userBootSettings, bootStrings] = data;

            let featureOverrides: string[] =
                userBootSettings?.cdnSettings?.ExtraSettings?.featureOverrides || [];
            let featureFlags = (userBootSettings.features || []).concat(featureOverrides);
            initializeFeatureFlags(featureFlags);

            setUserConfiguration(sessionData.owaUserConfig);
            sessionData.owaUserConfig = getUserConfiguration();
            stashSessionDataResponses(sessionData);

            const preRenderPromises: PromiseWithKey<unknown>[] = options.prerenderPromises || [];
            preRenderPromises.push({
                promise: initializeLocales(
                    bootStrings,
                    options.initializeLocalization,
                    userBootSettings
                )
                    .catch(e => {
                        addIfNotNull(e, 'source', 'InitLoc');
                        addIfNotNull(e, 'status', e.httpStatus);
                        return Promise.reject(e);
                    })
                    .then(() => {
                        initializeWindowTitle(options.getWindowTitle);
                    }),
                key: 'lcl',
            });

            updateServiceConfig({
                useJsonNetSerializer: isFeatureEnabled('fwk-useJsonNetSerializer'),
            });

            initializeDateTime(
                getUserConfiguration(),
                options.initialConfig.shouldInitializeTimeZoneAnonymously
            );

            // Start the application
            options.initializeState();

            // Router initialization depends on user configuration, so initialize it after
            await initializeRouting(options.registerRoutes, options.routerOptions);

            // Await any additional promises before final render
            await trackBottleneck('ren', preRenderPromises);

            // start downloading the bundle
            options.postLazyAction?.import();

            // Return a Promise to render the initial app state
            let asyncRenderPromise = markFunction(asyncRender, 'ren')(
                <ErrorBoundary
                    fullErrorComponent={options.fullErrorComponent}
                    onError={options.onComponentError}>
                    {options.renderMainComponent()}
                </ErrorBoundary>,
                document.getElementById('app')
            );

            return asyncRenderPromise;
        })
        .then(() => {
            // this should be removed when we remove preboot
            const onBootComplete = (window as any).onBootComplete;
            onBootComplete?.();

            setTimeout(() => {
                options.postLazyAction?.importAndExecute();
                unblockLazyLoadCallbacks();
                lazySetupSharedPostBoot.importAndExecute(
                    options.analyticsOptions,
                    options.swConfig?.app,
                    options.swConfig?.feature
                );
            }, DelayAfterRenderInMilliseconds);
        });

    return renderPromise;
}

function addIfNotNull(error: Error, column: string, value: any) {
    if (error[column] === undefined) {
        error[column] = value;
    }
}
